/**
 * Centralized navigation helpers for external redirects and full-page reloads.
 *
 * When migrating to Nuxt 4, replace these implementations with:
 * - navigateTo(url, { external: true }) for external redirects
 * - navigateTo(path, { external: true }) for full-page reloads
 */

let redirecting = false;

const RECOVERY_RELOAD_KEY = 'bn:last-recovery-reload';
const RECOVERY_RELOAD_WINDOW_MS = 10_000;

/**
 * Whether an intentional full-page redirect is currently in flight.
 * While true, stale-chunk auto-recovery must not reload the page: the
 * redirect aborts pending module fetches, and a recovery reload would
 * cancel the redirect and loop back to the current origin.
 */
export function isRedirecting() {
    return redirecting;
}

/**
 * Gate for stale-chunk auto-recovery reloads. Returns false while a
 * redirect is in flight, and rate-limits to one reload per window so a
 * reload that fails to fix the problem can never loop. Records the
 * attempt when it returns true.
 */
export function canRecoveryReload() {
    if (typeof window === 'undefined' || redirecting) {
        return false;
    }

    try {
        const last = Number(sessionStorage.getItem(RECOVERY_RELOAD_KEY)) || 0;
        if (Date.now() - last < RECOVERY_RELOAD_WINDOW_MS) {
            return false;
        }
        sessionStorage.setItem(RECOVERY_RELOAD_KEY, String(Date.now()));
    } catch {
        // sessionStorage unavailable — allow the reload, still guarded by the flag
    }

    return true;
}

/**
 * Redirect to an external URL (replaces current history entry).
 * Used for auth gateway, login, and other cross-domain redirects.
 */
export function redirectTo(url) {
    if (typeof window !== 'undefined') {
        redirecting = true;
        location.replace(url);
    }

    // Router guards must return this promise to park the navigation while
    // the page unloads — returning false instead would reject it with a
    // messageless NavigationFailure (unhandled rejection).
    return new Promise(() => {});
}

/**
 * Navigate to a URL with a full page reload (creates new history entry).
 * Used for token swaps, impersonation, and state resets.
 */
export function hardNavigate(url) {
    if (typeof window !== 'undefined') {
        redirecting = true;
        window.location.href = url;
    }
}

/**
 * Build a gateway redirect URL for auth flows.
 */
export function buildGatewayURL(gatewayBase, { continueURL, platform } = {}) {
    const url = new URL(gatewayBase);
    if (continueURL) url.searchParams.set('continue', continueURL);
    if (platform) url.searchParams.set('platform', platform);
    return url.toString();
}

/**
 * Get the current page URL, SSR-safe.
 */
export function currentURL() {
    return typeof window !== 'undefined' ? location.href : '/';
}

/**
 * Get the current origin, SSR-safe.
 */
export function currentOrigin() {
    return typeof window !== 'undefined' ? location.origin : '';
}
